import { board, ui, select } from '@owenscorning/pcb.alpha';
import _ from 'lodash';
import { useState, useEffect } from 'react';
import ReactOnRails from 'react-on-rails';
import { Provider } from 'react-redux';
import ResizeObserver from 'resize-observer-polyfill';

import Library from './Library';
import Postie from './Postie';
import Integration from './Redux';
import joinPath from '../../helpers/joinPath';
import GlobalThemeStyles from '../../themes/globalThemeStyles';
// connecting to redux manually for now
import { cmsRenderConnector } from '../PageBuilder/redux/connector';
import { navigate } from '../helpers/sticky_nav';
import { connectLocation } from '../location/LocationConnectors';

const Renderer = board`Builder::Renderer`('0.0.1')(
  Library,
  ({ editable, contentType, content_info, isComparison }) => {
    const Type = _.get(
      UI.Types,
      contentType.replace('Cms::Content::', '').split('::')
    )(UI);
    const Editor = {};

    if (editable) {
      Editor.bridge = Postie.Child();
      Editor.bridge.on('select', selection => Board.select?.(selection, false));
      Editor.bridge.on('data', data => Editor.receive?.(data));
      Editor.bridge.send('ready');
    } else if (isComparison) {
      Editor.bridge = Postie.Child();
      Editor.bridge.send('ready');
    }

    // TODO: use the localized name (from content_info.language_summary should be already passed)
    const Content = {
      name: content_info?.name,
      info: content_info,
    };

    return { Type, Content, Editor };
  },
  ({
    Type,
    Content,
    Editor,
    editable,
    locale: { code: language },
    contentType,
    displayContents,
    metadata,
    linkedObject,
    isComparison,
    zipFile,
  }) => {
    const { view, variables, ...definition } = Type;
    const build = {
      type: contentType,
      name: Content.name,
      site: Content.info?.site,
      location: Content.info?.path,
      slug: Content.info?.slug,
      path: joinPath(Content.info?.path, Content.info?.slug),
      language,
    };

    const pageContents = _.cloneDeep(displayContents) || {};
    const pageMetadata = _.cloneDeep(metadata) || {};

    const [value, setValue] = useState(
      Type.read({
        build,
        contents: pageContents,
        metadata: pageMetadata,
        zipFile,
      })
    );
    Editor.receive = setValue;

    if (editable || isComparison) {
      useEffect(() => {
        const observer = new ResizeObserver(([body]) =>
          Editor.bridge.send('height', body.target.offsetHeight)
        );
        observer.observe(document.getElementsByTagName('html')[0]);
        return () => observer.disconnect();
      }, []);
    } else {
      const { adobeDataLayer } = window;
      if (adobeDataLayer && digitalData) {
        let analyticsInitData = {
          event: 'init',
          page: {
            page_name: digitalData.page.pageName,
            referring_page_name: digitalData.page.referringPageName,
            referring_url: digitalData.page.referringUrl,
          },
          site: {
            language: digitalData.site.language,
            site_domain: digitalData.site.siteDomain,
            site_name: digitalData.site.siteName,
          },
          enabled_feature_flags:
            REDUX_INITIAL?.featureFlag?.enabledFeatureFlags || [],
        };
        if (digitalData.user?.hashedAcct) {
          // check how to verify if user is present
          analyticsInitData = Object.assign(analyticsInitData, {
            visitor: {
              city: REDUX_INITIAL?.location?.address?.city,
              country: REDUX_INITIAL?.location?.address?.country,
              country_iso: REDUX_INITIAL?.location?.address?.country_iso,
              region: REDUX_INITIAL?.location?.address?.region,
              time_zone: REDUX_INITIAL?.location?.address?.time_zone,
              zip_code: REDUX_INITIAL?.location?.address?.zip_code,
              zip_code_entered: REDUX_INITIAL?.location?.zip,
              zone: REDUX_INITIAL?.location?.address?.zone,
            },
          });
        }

        adobeDataLayer.push(analyticsInitData);
      }
      useEffect(() => {
        if (window.location.hash) {
          const hash = window.location.hash.substring(1);
          navigate(hash);
        }
      }, []);
    }

    return {
      value: Library.User.Filter(value),
      variables: _.merge(
        {},
        {
          editable,
          definition,
          language,
          bridge: Editor.bridge,
          settings: value.metadata?.settings || {},
          build,
          selection: null,
          select: (selection, push = true) => {
            if (!editable) return;
            if (push) Editor.bridge.send('select', selection);
            const element = document.getElementById(selection);
            if (element)
              Editor.bridge.send(
                'position',
                element.getBoundingClientRect().top + window.scrollY
              );
            Board.Set({ selection });
          },
        },
        variables
      ),
      schematic: {
        modals: ui`Modals`({ open: select`~modal/stack` }),
        ...view,
      },
    };
  }
);

const ConnectedRenderer = cmsRenderConnector(connectLocation(Renderer));
const ProvidedRenderer = props => (
  <Provider store={ReactOnRails.getStore('CmsStore')}>
    <GlobalThemeStyles />
    <ConnectedRenderer {...props} />
  </Provider>
);

export default ProvidedRenderer;
